import React from 'react';
import Intercom from '@svg/intercom';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { GetCollectionsParams, getIntercomCollections, IntercomLoaderCollection } from '@requests/loaders';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../../../styles';
import { useParams } from 'react-router-dom';
import { RequestError } from '@requests/common';

import {
  Alert,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha,
} from '@mui/material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;

  & .MuiListItemText-secondary {
    max-width: 97%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  & .MuiListItemIcon-root {
    min-width: 40px;
  }
`;

const ListContainer = styled.div`
  display: flex;
  width: 97%;
`;

type CollectionProps = {
  collection: IntercomLoaderCollection;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
};

const CollectionListItem: React.FC<CollectionProps> = ({ collection, selected, setSelected }) => {
  const handleCheckboxToggle = () => {
    if (selected.includes(collection.name)) setSelected(selected.filter((id) => id !== collection.name));
    else setSelected(selected.concat(collection.name));
  };

  return (
    <ListItem disablePadding sx={{ marginBottom: '4px' }}>
      <ListItemButton onClick={() => handleCheckboxToggle()} dense sx={{ borderRadius: '8px' }}>
        <ListItemIcon>
          <Checkbox edge='start' checked={selected.includes(collection.name)} tabIndex={-1} disableRipple />
        </ListItemIcon>
        <ListItemText primary={collection.name} secondary={collection.description} />
      </ListItemButton>
    </ListItem>
  );
};

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  };
}

export const IntercomCollections: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const theme = useTheme();

  const { service_id } = useParams();
  const { secretIdentifier: secretName, selected, setSelected } = configuration;

  const {
    data: collections = [],
    isLoading,
    error,
  } = useQuery<IntercomLoaderCollection[], RequestError, IntercomLoaderCollection[], [string, GetCollectionsParams]>({
    queryKey: ['intercom-collections', { secret_identifier: secretName, service_id: service_id as string }],
    queryFn: getIntercomCollections,
    enabled: Boolean(secretName && service_id),
    retry: false,
  });

  if (error) {
    return (
      <Modal
        open={open === 'intercom' && page === 2}
        setOpen={reset}
        page={page + 1}
        total_pages={4}
        background={alpha(theme.text.standard, 0.06)}
        header='Intercom Error'
        icon={<Intercom color='#0057ff' height={21} />}
        body={<Alert severity='error'>{error?.response?.data?.error || error?.message}</Alert>}
        actions={
          <Stack direction='row' spacing={1}>
            <Button onClick={reset}>Cancel</Button>
            <Button customColor='red' onClick={() => setPage(0)}>
              Change Authentication Key
            </Button>
          </Stack>
        }
      />
    );
  }

  const body = (
    <Container>
      <p>
        Select all the collections in your Intercom account that you would like to include in this loader. Selecting a
        collection will pull all articles from that collection and upload them to your bucket.
      </p>
      {isLoading && (
        <Stack spacing={1.5} padding='4rem 0rem' width='100%' display='flex' alignItems='center'>
          <p>Loading Intercom Collections</p>
          <CircularProgress size={16} sx={{ color: theme.primary }} />
        </Stack>
      )}
      {!isLoading && (
        <ListContainer>
          <List sx={{ width: '100%' }}>
            {collections.map((collection) => (
              <CollectionListItem
                key={collection.name}
                collection={collection}
                selected={selected}
                setSelected={setSelected}
              />
            ))}
          </List>
        </ListContainer>
      )}
    </Container>
  );

  const allSelected = collections?.length === selected.length;

  const actions = (
    <Stack width='100%' direction='row' display='flex' justifyContent='space-between'>
      {allSelected ? (
        <Button onClick={() => setSelected([])}>Deselect All</Button>
      ) : (
        <Button onClick={() => setSelected(collections.map((collection) => collection.name))} disabled={isLoading}>
          Select All
        </Button>
      )}
      <Stack spacing={1} direction='row'>
        <Button onClick={() => setPage(1)}>Back</Button>
        <Button disabled={selected.length === 0} customColor='primary' onClick={() => setPage(3)}>
          Load Categories
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Modal
      open={open === 'intercom' && page === 2}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Intercom'
      icon={<Intercom color={theme.text.standard} height={21} />}
      body={body}
      actions={actions}
    />
  );
};
